import type { FC, RefObject } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Stack, useTheme } from '@mui/material';
import type { ChartData } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import type { WidgetChartConfigCamelized } from 'src/types/dashboard';
import { hasHorizontalScrollbar } from 'src/helper/helper';
import { getOptions } from 'src/components/dashboard/charts/LineChart/chartConfig';
import type { AggregateResult } from 'src/components/followUp/common/types';

import { condenseGaps, getLabelList, getValueList } from './helper';

import { styles } from './LineChart.styles';

interface LineChartProps {
  aggregateResults: AggregateResult[];
  chartConfig: WidgetChartConfigCamelized;
}

const LineChart: FC<LineChartProps> = ({ aggregateResults, chartConfig }) => {
  const { colors } = useTheme();

  const scrollableChartRef: RefObject<HTMLElement> = useRef(null);
  const nonScrollableChartPadding = 2;
  const [scrollableChartPadding, setScrollableChartPadding] = useState(
    nonScrollableChartPadding,
  );

  // prevent vertical content shifts by horizontal scrollbar (scrollbar-gutter only applies to vertical scrollbars)
  useLayoutEffect(() => {
    if (hasHorizontalScrollbar(scrollableChartRef)) {
      setScrollableChartPadding(0);
    } else {
      setScrollableChartPadding(nonScrollableChartPadding);
    }
  }, [scrollableChartRef, aggregateResults]);

  const [labelList, valueList] = useMemo(() => {
    if (!Array.isArray(aggregateResults))
      return [[] as string[], [] as number[]];

    const { data, labels } = condenseGaps(
      getValueList(aggregateResults),
      getLabelList(aggregateResults),
    );

    // Add empty values for an extra padding on the start and end of the chart
    return [
      ['', ...labels, ''],
      [NaN, ...data, NaN],
    ];
  }, [aggregateResults]);

  const chartData: ChartData<'line', number[], string | string[]> = useMemo(
    () => ({
      labels: labelList,
      datasets: [
        {
          data: valueList,
          borderColor: colors.secondary[400],
          spanGaps: true,
          segment: {
            borderColor: (ctx) =>
              ctx.p0.skip || ctx.p1.skip ? colors.neutral[300] : undefined,
            borderDash: (ctx) =>
              ctx.p0.skip || ctx.p1.skip ? [6, 6] : undefined,
          },
        },
      ],
    }),
    [labelList, valueList, colors.secondary],
  );

  const dataGaps = valueList.reduce(
    (acc: number[], value, i) =>
      isNaN(value) && i > 0 && i + 1 < valueList.length ? [...acc, i] : acc,
    [],
  );

  const optionsYAxisChart = getOptions(colors, chartConfig, dataGaps, {
    gridOptions: { display: false },
  });
  const optionsXAxisAndDataChart = getOptions(colors, chartConfig, dataGaps, {
    gridOptions: { tickLength: -50 },
    tickOptions: { color: 'transparent' },
  });

  // workaround: use two overlapping charts for one horizontal scroll chart with fixed y-axis
  return (
    <Stack direction="row" sx={styles.chartStack}>
      <Box sx={styles.yAxisChartOuterWrapper}>
        <Box
          sx={{
            minWidth: `${valueList.length * 5}rem`,
            pb: nonScrollableChartPadding,
            ...styles.yAxisChartInnerWrapper,
          }}
        >
          <Line
            height="100%"
            width="auto"
            plugins={[ChartDataLabels]}
            data={chartData}
            options={optionsYAxisChart}
          />
        </Box>
      </Box>
      <Box sx={styles.xAxisAndDataChartOuterWrapper} ref={scrollableChartRef}>
        <Box
          sx={[
            {
              minWidth: `${valueList.length * 5}rem`,
              pb: scrollableChartPadding,
            },
            styles.xAxisAndDataChartInnerWrapper,
          ]}
        >
          <Line
            height="100%"
            width="auto"
            plugins={[ChartDataLabels]}
            data={chartData}
            options={optionsXAxisAndDataChart}
          />
        </Box>
      </Box>
    </Stack>
  );
};

export default LineChart;
